import { useCallback, useEffect, useState } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';

export type DetailCardId =
  | 'customer_vehicle'
  | 'notes'
  | 'services'
  | 'approval'
  | 'service_book'
  | 'parts_orders'
  | 'attachments'
  | 'payments'
  | 'courtesy'
  | 'events';

const CARD_ORDER_KEY = 'vs-workorder-detail-card-order';
const CARD_EXPANDED_KEY = 'vs-workorder-detail-card-expanded';
const CARD_HANDLES_VISIBLE_KEY = 'vs-workorder-detail-card-handles-visible';
const ACCORDION_KEY = 'os-buddy-workorder-detail-accordion';

/** Old card ids → the card they now live in. First occurrence keeps its slot. */
const LEGACY_CARD_ID_MAP: Record<string, DetailCardId> = {
  customer: 'customer_vehicle',
  vehicle: 'customer_vehicle',
  parties: 'customer_vehicle',
  vehicle_location: 'customer_vehicle',
  vehicle_history: 'customer_vehicle',
};

export const DEFAULT_CARD_ORDER: DetailCardId[] = [
  'customer_vehicle',
  'notes',
  'services',
  // Customer approval (PF-083). Stored layouts get it appended by mergeCardOrder.
  'approval',
  'service_book',
  'parts_orders',
  'attachments',
  'payments',
  // Courtesy car (PF-120). New for stored layouts too: mergeCardOrder appends ids missing from a saved order.
  'courtesy',
  'events',
];

const DEFAULT_EXPANDED_CARDS: Record<DetailCardId, boolean> = {
  customer_vehicle: true,
  notes: true,
  services: true,
  approval: true,
  service_book: true,
  parts_orders: true,
  attachments: false,
  payments: false,
  courtesy: false,
  events: false,
};

function isDetailCardId(value: string): value is DetailCardId {
  return DEFAULT_CARD_ORDER.includes(value as DetailCardId);
}

/**
 * Map legacy card ids onto the current set. Duplicates are dropped and the
 * first mapped occurrence keeps its original position so existing users do
 * not see Customer/Vehicle jump to the bottom of the page.
 */
export function migrateCardIds(ids: unknown[]): DetailCardId[] {
  const seen = new Set<DetailCardId>();
  const result: DetailCardId[] = [];
  for (const id of ids) {
    if (typeof id !== 'string') continue;
    const mapped = LEGACY_CARD_ID_MAP[id] ?? id;
    if (!isDetailCardId(mapped) || seen.has(mapped)) continue;
    seen.add(mapped);
    result.push(mapped);
  }
  return result;
}

/**
 * Split customer/vehicle cards, older tab-cards, and the previous combined
 * card all map onto `customer_vehicle`. Later keys OR in `true` so a
 * previously open source cannot collapse info that used to be visible.
 */
export function migrateExpandedCards(
  parsed: Record<string, unknown>,
): Partial<Record<DetailCardId, boolean>> {
  const result: Partial<Record<DetailCardId, boolean>> = {};
  const assign = (id: DetailCardId, value: boolean) => {
    result[id] = result[id] === true || value;
  };
  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value !== 'boolean') continue;
    const mapped = LEGACY_CARD_ID_MAP[key] ?? key;
    if (isDetailCardId(mapped)) assign(mapped, value);
  }
  return result;
}

function readStoredCardOrder(): DetailCardId[] {
  try {
    const stored = localStorage.getItem(CARD_ORDER_KEY);
    if (!stored) return DEFAULT_CARD_ORDER;
    const parsed: unknown = JSON.parse(stored);
    return Array.isArray(parsed) ? migrateCardIds(parsed) : DEFAULT_CARD_ORDER;
  } catch {
    return DEFAULT_CARD_ORDER;
  }
}

function readStoredHandlesVisible(): boolean {
  try {
    return localStorage.getItem(CARD_HANDLES_VISIBLE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Accordion mode (#3364) defaults on; only an explicit `false` turns it off. */
function readStoredAccordion(): boolean {
  try {
    return localStorage.getItem(ACCORDION_KEY) !== 'false';
  } catch {
    return true;
  }
}

/**
 * Next expanded state for a toggle. In accordion mode opening a card closes
 * every other card; closing one never opens anything.
 */
export function toggleExpandedCard(
  current: Record<DetailCardId, boolean>,
  cardId: DetailCardId,
  accordion: boolean,
): Record<DetailCardId, boolean> {
  const opening = !current[cardId];
  if (!accordion || !opening) return { ...current, [cardId]: opening };
  const next = { ...current };
  for (const id of Object.keys(next) as DetailCardId[]) next[id] = id === cardId;
  return next;
}

function readStoredExpandedCards(): Record<DetailCardId, boolean> {
  try {
    const stored = localStorage.getItem(CARD_EXPANDED_KEY);
    if (!stored) return DEFAULT_EXPANDED_CARDS;
    const parsed: unknown = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return DEFAULT_EXPANDED_CARDS;
    }
    return {
      ...DEFAULT_EXPANDED_CARDS,
      ...migrateExpandedCards(parsed as Record<string, unknown>),
    };
  } catch {
    return DEFAULT_EXPANDED_CARDS;
  }
}

export function mergeCardOrder(
  order: DetailCardId[],
  available: DetailCardId[],
): DetailCardId[] {
  const filtered = order.filter((id) => available.includes(id));
  const missing = available.filter((id) => !filtered.includes(id));
  return [...filtered, ...missing];
}

/**
 * Which detail cards this tenant may show (#2646). Service-book/checklists
 * and parts-ordering honour the module switches; the delivery report is
 * *not* a card — the page keeps `DeliveryReportButton` reachable when
 * `service_book` is hidden. Permissions still apply inside each card.
 */
export function availableWorkOrderDetailCards(opts: {
  hasPartsOrders: boolean;
  serviceBookEnabled: boolean;
  partsOrderingEnabled: boolean;
}): DetailCardId[] {
  return DEFAULT_CARD_ORDER.filter((cardId) => {
    if (cardId === 'service_book') return opts.serviceBookEnabled;
    if (cardId === 'parts_orders') return opts.hasPartsOrders && opts.partsOrderingEnabled;
    return true;
  });
}

export interface WorkOrderDetailCardLayout {
  cardOrder: DetailCardId[];
  expandedCards: Record<DetailCardId, boolean>;
  handlesVisible: boolean;
  accordion: boolean;
  toggleCard: (cardId: DetailCardId) => void;
  toggleHandlesVisible: () => void;
  toggleAccordion: () => void;
  reorderCards: (event: DragEndEvent, availableIds: DetailCardId[]) => void;
}

export function useWorkOrderDetailCardLayout(): WorkOrderDetailCardLayout {
  const [cardOrder, setCardOrder] = useState<DetailCardId[]>(() => readStoredCardOrder());
  const [expandedCards, setExpandedCards] = useState<Record<DetailCardId, boolean>>(() =>
    readStoredExpandedCards(),
  );
  const [handlesVisible, setHandlesVisible] = useState<boolean>(() => readStoredHandlesVisible());
  const [accordion, setAccordion] = useState<boolean>(() => readStoredAccordion());

  const toggleCard = useCallback(
    (cardId: DetailCardId) => {
      setExpandedCards((current) => toggleExpandedCard(current, cardId, accordion));
    },
    [accordion],
  );

  const toggleAccordion = useCallback(() => {
    setAccordion((current) => !current);
  }, []);

  const toggleHandlesVisible = useCallback(() => {
    setHandlesVisible((current) => !current);
  }, []);

  const reorderCards = useCallback(
    (event: DragEndEvent, availableIds: DetailCardId[]) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      setCardOrder((current) => {
        const sanitized = mergeCardOrder(current, availableIds);
        const oldIndex = sanitized.indexOf(active.id as DetailCardId);
        const newIndex = sanitized.indexOf(over.id as DetailCardId);
        if (oldIndex === -1 || newIndex === -1) return sanitized;
        return arrayMove(sanitized, oldIndex, newIndex);
      });
    },
    [],
  );

  useEffect(() => {
    try {
      localStorage.setItem(CARD_ORDER_KEY, JSON.stringify(cardOrder));
    } catch {
      // Ignore localStorage write failures.
    }
  }, [cardOrder]);

  useEffect(() => {
    try {
      localStorage.setItem(CARD_HANDLES_VISIBLE_KEY, String(handlesVisible));
    } catch {
      // Ignore localStorage write failures.
    }
  }, [handlesVisible]);

  useEffect(() => {
    try {
      localStorage.setItem(ACCORDION_KEY, String(accordion));
    } catch {
      // Ignore localStorage write failures.
    }
  }, [accordion]);

  useEffect(() => {
    try {
      localStorage.setItem(CARD_EXPANDED_KEY, JSON.stringify(expandedCards));
    } catch {
      // Ignore localStorage write failures.
    }
  }, [expandedCards]);

  return {
    cardOrder,
    expandedCards,
    handlesVisible,
    accordion,
    toggleCard,
    toggleHandlesVisible,
    toggleAccordion,
    reorderCards,
  };
}
